import type { MarketingResource } from "creso-core/types";
import { ChevronLeft, ChevronRight, DownloadIcon, Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import {
  Button,
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  cn,
} from "web-design-system";

type WebStoryPreviewCarouselProps = {
  item: MarketingResource;
  onSlideChange?: (index: number) => void;
  onDownloadImages: () => void;
  onDownloadVideo: () => void;
  isDownloadingImages?: boolean;
  isDownloadingVideo?: boolean;
  canDownloadVideo?: boolean;
};

function StoryProgressBar({
  slideCount,
  selectedIndex,
}: {
  slideCount: number;
  selectedIndex: number;
}) {
  if (slideCount <= 1) {
    return null;
  }

  return (
    <div
      className="pointer-events-none absolute inset-x-3 top-3 z-10 flex gap-1"
      aria-hidden
    >
      {Array.from({ length: slideCount }, (_, index) => (
        <div
          key={index}
          className={cn(
            "h-1 flex-1 rounded-full transition-opacity duration-200",
            index <= selectedIndex ? "bg-black/50" : "bg-muted-foreground/25"
          )}
        />
      ))}
    </div>
  );
}

function StorySlide({
  attachment,
  title,
  index,
}: {
  attachment: MarketingResource["attachments"][number];
  title: string;
  index: number;
}) {
  const src = attachment.attachment;

  return (
    <div className="relative aspect-[9/16] w-full overflow-hidden rounded-none">
      <img
        src={src}
        alt={`${title} slide ${index + 1}`}
        className="size-full object-cover"
      />
    </div>
  );
}

export function WebStoryPreviewCarousel({
  item,
  onSlideChange,
  onDownloadImages,
  onDownloadVideo,
  isDownloadingImages = false,
  isDownloadingVideo = false,
  canDownloadVideo = false,
}: WebStoryPreviewCarouselProps) {
  const [api, setApi] = useState<CarouselApi>();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const attachments =
    item.attachments?.filter(
      (attachment) => attachment.attachment_type !== "video"
    ) ?? [];
  const slideCount = attachments.length;

  const syncCarouselState = useCallback(() => {
    if (!api) {
      return;
    }

    const index = api.selectedScrollSnap();
    setSelectedIndex(index);
    setCanScrollPrev(api.canScrollPrev());
    setCanScrollNext(api.canScrollNext());
    onSlideChange?.(index);
  }, [api, onSlideChange]);

  useEffect(() => {
    if (!api) {
      return undefined;
    }

    syncCarouselState();
    api.on("select", syncCarouselState);
    api.on("reInit", syncCarouselState);

    return () => {
      api.off("select", syncCarouselState);
      api.off("reInit", syncCarouselState);
    };
  }, [api, syncCarouselState]);

  useEffect(() => {
    api?.scrollTo(0);
    setSelectedIndex(0);
    onSlideChange?.(0);
  }, [item.id, api, onSlideChange]);

  if (slideCount === 0) {
    return null;
  }

  const showNav = slideCount > 1;

  return (
    <div className="flex w-full flex-col bg-card">
      <div className="relative min-w-0">
        <StoryProgressBar
          slideCount={slideCount}
          selectedIndex={selectedIndex}
        />
        <Carousel
          setApi={setApi}
          opts={{ loop: slideCount > 1 }}
          className="w-full"
        >
          <CarouselContent className="-ml-0">
            {attachments.map((attachment, index) => (
              <CarouselItem key={attachment.id} className="pl-0">
                <StorySlide
                  attachment={attachment}
                  title={item.title}
                  index={index}
                />
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>

        {showNav ? (
          <div
            className={cn(
              "group/nav-prev absolute inset-y-0 left-0 z-10 flex w-1/4 items-center justify-start pl-2",
              !canScrollPrev && "pointer-events-none"
            )}
          >
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="size-8 shrink-0 rounded-full border-border/60 bg-foreground text-background opacity-30 shadow-sm transition-opacity group-hover/nav-prev:opacity-100 hover:bg-foreground hover:text-background disabled:opacity-25 [&_svg]:size-5"
              disabled={!canScrollPrev}
              onClick={() => api?.scrollPrev()}
              aria-label="Previous slide"
            >
              <ChevronLeft />
            </Button>
          </div>
        ) : null}

        {showNav ? (
          <div
            className={cn(
              "group/nav-next absolute inset-y-0 right-0 z-10 flex w-1/4 items-center justify-end pr-2",
              !canScrollNext && "pointer-events-none"
            )}
          >
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="size-8 shrink-0 rounded-full border-border/60 bg-foreground/80 text-background opacity-30 shadow-sm transition-opacity group-hover/nav-next:opacity-100 hover:bg-foreground hover:text-background disabled:opacity-25 [&_svg]:size-5"
              disabled={!canScrollNext}
              onClick={() => api?.scrollNext()}
              aria-label="Next slide"
            >
              <ChevronRight />
            </Button>
          </div>
        ) : null}
      </div>

      <div className="flex gap-3 p-3 bg-background">
        <Button
          type="button"
          variant="outline"
          className="flex-1"
          onClick={onDownloadImages}
          disabled={isDownloadingImages || isDownloadingVideo}
        >
          {isDownloadingImages ? (
            <Loader2 className="size-4 animate-spin" aria-hidden />
          ) : (
            <>
              <DownloadIcon />
              Images
            </>
          )}
        </Button>
        <Button
          type="button"
          className="flex-1"
          onClick={onDownloadVideo}
          disabled={
            isDownloadingVideo || isDownloadingImages || !canDownloadVideo
          }
        >
          {isDownloadingVideo ? (
            <Loader2 className="size-4 animate-spin" aria-hidden />
          ) : (
            <>
              <DownloadIcon />
              Video
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
